import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, PLATFORM_ID, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { $t, updatePreset, updateSurfacePalette } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { PrimeNG } from 'primeng/config';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LayoutService } from '../service/layout.service';

@Component({
    selector: 'app-configurator',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectButtonModule],
    template: `
        <div class="flex flex-col gap-4">
            <!-- Removed Primary picker: we use custom brand tokens instead of Aura ramps -->

            <div>
                <span class="text-sm text-muted-color font-semibold">Surface</span>
                <div class="pt-2 flex gap-2 flex-wrap justify-start">
                    @for (surface of surfaces; track surface.name) {
                        <button
                            type="button"
                            [title]="surface.name"
                            (click)="updateSurface($event, surface)"
                            [ngClass]="{
                                'outline-primary': selectedSurfaceColor() ? selectedSurfaceColor() === surface.name : layoutService.layoutConfig().darkTheme ? surface.name === 'zinc' : surface.name === 'slate'
                            }"
                            class="border-none w-5 h-5 rounded-full p-0 cursor-pointer outline-none outline-offset-1"
                            [style]="{
                                'background-color': surface?.name === 'noir' ? 'var(--text-color)' : surface?.palette?.['500']
                            }"
                        ></button>
                    }
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <span class="text-sm text-muted-color font-semibold">Presets</span>
                <!-- Lock to Aura (single preset). You can even hide this control if you don't want switching. -->
                <p-selectbutton [options]="presets" [ngModel]="selectedPreset()" (ngModelChange)="onPresetChange($event)" [allowEmpty]="false" size="small" />
            </div>

            @if (showMenuModeButton()) {
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-muted-color font-semibold">Menu Mode</span>
                    <p-selectbutton [ngModel]="menuMode()" (ngModelChange)="onMenuModeChange($event)" [options]="menuModeOptions" [allowEmpty]="false" size="small" />
                </div>
            }
        </div>
    `,
    host: {
        class: 'hidden absolute top-[3.25rem] right-0 w-72 p-4 bg-surface-0 dark:bg-surface-900 border border-surface rounded-border origin-top shadow-[0px_3px_5px_rgba(0,0,0,0.02),0px_0px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.08)]'
    }
})
export class AppConfigurator {
    router = inject(Router);
    config: PrimeNG = inject(PrimeNG);
    layoutService: LayoutService = inject(LayoutService);
    platformId = inject(PLATFORM_ID);
    primeng = inject(PrimeNG);

    // Lock to Aura; if you don’t want users to switch presets, you can remove the UI
    presets = [{ label: 'Aura', value: 'Aura' as const }];

    showMenuModeButton = signal(!this.router.url.includes('auth'));

    menuModeOptions = [
        { label: 'Static', value: 'static' },
        { label: 'Overlay', value: 'overlay' }
    ];

    // Surfaces stay (optional). Remove if you want a fixed neutral ramp.
    surfaces: SurfacesType[] = [
        {
            name: 'slate',
            palette: {
                0: '#ffffff',
                50: '#f8fafc',
                100: '#f1f5f9',
                200: '#e2e8f0',
                300: '#cbd5e1',
                400: '#94a3b8',
                500: '#64748b',
                600: '#475569',
                700: '#334155',
                800: '#1e293b',
                900: '#0f172a',
                950: '#020617'
            }
        },
        {
            name: 'zinc',
            palette: {
                0: '#ffffff',
                50: '#fafafa',
                100: '#f4f4f5',
                200: '#e4e4e7',
                300: '#d4d4d8',
                400: '#a1a1aa',
                500: '#71717a',
                600: '#52525b',
                700: '#3f3f46',
                800: '#27272a',
                900: '#18181b',
                950: '#09090b'
            }
        }
        // ... keep others if you use them
    ];

    // Computed signals
    selectedSurfaceColor = computed(() => this.layoutService.layoutConfig().surface);
    selectedPreset = computed(() => this.layoutService.layoutConfig().preset);
    menuMode = computed(() => this.layoutService.layoutConfig().menuMode);

    private themeInitialized = false;

    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) return;

        // Force preset to Aura once
        if (this.layoutService.layoutConfig().preset !== 'Aura') {
            this.layoutService.layoutConfig.update((s) => ({ ...s, preset: 'Aura' }));
        }

        // Initialize theme once (Aura + custom brand + selected surface)
        if (!this.themeInitialized) {
            this.applyAuraWithCustomBrand();
            this.themeInitialized = true;
        }
    }

    // 1) Define your custom brand tokens (replace with your colors)
    private brand = {
        color: '#005595', // main brand color
        hover: '#0E5CA4', // hover state
        active: '#0C4E8B', // active/pressed
        text: '#ffffff', // contrast text on brand background
        lightBg: 'color-mix(in srgb, #0F6CBD, transparent 90%)',
        lightBgFocus: 'color-mix(in srgb, #0F6CBD, transparent 82%)'
    };

    // 2) Build the preset extension that uses custom brand tokens (no primary.100/etc.)
    private getCustomPresetExt() {
        return {
            semantic: {
                // Keep primary object present but unused (optional)
                primary: {},

                // Your custom token namespace
                brand: this.brand,

                // Map components’ semantic usage to your brand tokens
                colorScheme: {
                    light: {
                        primary: {
                            color: '{brand.color}',
                            contrastColor: '{brand.text}',
                            hoverColor: '{brand.hover}',
                            activeColor: '{brand.active}'
                        },
                        highlight: {
                            background: '{brand.lightBg}',
                            focusBackground: '{brand.lightBgFocus}',
                            color: '{brand.color}',
                            focusColor: '{brand.color}'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{brand.color}',
                            contrastColor: '{surface.900}',
                            hoverColor: '{brand.hover}',
                            activeColor: '{brand.active}'
                        },
                        highlight: {
                            background: 'color-mix(in srgb, {brand.color}, transparent 84%)',
                            focusBackground: 'color-mix(in srgb, {brand.color}, transparent 76%)',
                            color: 'rgba(255,255,255,.87)',
                            focusColor: 'rgba(255,255,255,.87)'
                        }
                    }
                }
            }
        };
    }

    // 3) Apply Aura + custom brand + current (or default) surface palette
    private applyAuraWithCustomBrand() {
        const surfacePalette = this.surfaces.find((s) => s.name === this.selectedSurfaceColor())?.palette ?? this.surfaces.find((s) => s.name === 'slate')?.palette;

        $t().preset(Aura).preset(this.getCustomPresetExt()).surfacePalette(surfacePalette).use({ useDefaultOptions: true });
    }

    // Surface change handler (kept minimal)
    updateSurface(event: any, surface: SurfacesType) {
        this.layoutService.layoutConfig.update((s) => ({ ...s, surface: surface.name }));
        if (surface.palette) {
            updateSurfacePalette(surface.palette);
        }
        event?.stopPropagation?.();
    }

    // If you keep the Presets control visible, keep it locked to Aura
    onPresetChange(event: any) {
        // Always use Aura; re-apply your custom extension and current surface
        this.layoutService.layoutConfig.update((s) => ({ ...s, preset: 'Aura' }));
        this.applyAuraWithCustomBrand();
    }

    onMenuModeChange(event: string) {
        this.layoutService.layoutConfig.update((prev) => ({ ...prev, menuMode: event }));
    }
}

// Types moved to bottom for clarity
type KeyOfType<T> = keyof T extends infer U ? U : never;

type SurfacesType = {
    name?: string;
    palette?: {
        0?: string;
        50?: string;
        100?: string;
        200?: string;
        300?: string;
        400?: string;
        500?: string;
        600?: string;
        700?: string;
        800?: string;
        900?: string;
        950?: string;
    };
};
